<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type {
  FormBuilderField,
  VueformFieldType,
} from '@/types/form-builder-types'
import {
  FIELD_TYPE_LABELS,
  FIELD_TYPE_ICONS,
  FIELD_TYPE_CATEGORIES,
  FIELD_TYPE_DESCRIPTIONS,
} from '@/utils/field-icons'
import { generateFieldName, generateFieldId } from '@/utils/vueform-helpers'

const props = defineProps<{
  show: boolean
  field?: FormBuilderField
  existingFieldNames?: string[]
}>()

const emit = defineEmits<{
  close: []
  save: [field: FormBuilderField]
}>()

// Form state
const activeTab = ref<'basic' | 'validation' | 'options' | 'advanced'>('basic')
const fieldData = ref<FormBuilderField>(getDefaultField())

// Watch for field prop changes
watch(
  () => props.field,
  (newField) => {
    if (newField) {
      fieldData.value = JSON.parse(JSON.stringify(newField))
    } else {
      fieldData.value = getDefaultField()
    }
    activeTab.value = 'basic'
  },
  { immediate: true }
)

// Also watch show prop to reset when modal opens
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing && !props.field) {
      // Modal is opening in "add" mode - ensure clean slate
      fieldData.value = getDefaultField()
      activeTab.value = 'basic'
    }
  }
)

function getDefaultField(): FormBuilderField {
  return {
    id: generateFieldId(),
    name: '',
    type: 'text',
    label: '',
    placeholder: '',
    helpText: '',
    defaultValue: '',
    required: false,
    validation: {
      required: false,
    },
    options: [],
    columns: 12,
    readonly: false,
    hidden: false,
    order: 0,
  }
}

// Computed
const isEditMode = computed(() => !!props.field)
const modalTitle = computed(() =>
  isEditMode.value ? 'Edit Field' : 'Add Field'
)

const requiresOptions = computed(() =>
  ['select', 'multiselect', 'radio'].includes(fieldData.value.type)
)

const fieldTypeCategories = computed(() => FIELD_TYPE_CATEGORIES)

// Auto-generate field name from label
watch(
  () => fieldData.value.label,
  (newLabel) => {
    if (!isEditMode.value && newLabel) {
      fieldData.value.name = generateFieldName(newLabel)
    }
  }
)

// Methods
function handleSave() {
  // Validate
  const errors = validateField()
  if (errors.length > 0) {
    alert(errors.join('\n'))
    return
  }

  emit('save', fieldData.value)
  emit('close')
}

function validateField(): string[] {
  const errors: string[] = []

  if (!fieldData.value.label.trim()) {
    errors.push('Label is required')
  }

  if (!fieldData.value.name.trim()) {
    errors.push('Field name is required')
  }

  // Check for duplicate names
  if (
    props.existingFieldNames &&
    props.existingFieldNames.includes(fieldData.value.name) &&
    (!props.field || props.field.name !== fieldData.value.name)
  ) {
    errors.push('Field name already exists')
  }

  if (requiresOptions.value && (!fieldData.value.options || fieldData.value.options.length === 0)) {
    errors.push('At least one option is required for this field type')
  }

  return errors
}

function addOption() {
  if (!fieldData.value.options) {
    fieldData.value.options = []
  }
  fieldData.value.options.push({
    label: `Option ${fieldData.value.options.length + 1}`,
    value: `option_${fieldData.value.options.length + 1}`,
  })
}

function removeOption(index: number) {
  fieldData.value.options?.splice(index, 1)
}

function moveOptionUp(index: number) {
  if (index > 0 && fieldData.value.options) {
    const temp = fieldData.value.options[index]
    fieldData.value.options[index] = fieldData.value.options[index - 1]
    fieldData.value.options[index - 1] = temp
  }
}

function moveOptionDown(index: number) {
  if (fieldData.value.options && index < fieldData.value.options.length - 1) {
    const temp = fieldData.value.options[index]
    fieldData.value.options[index] = fieldData.value.options[index + 1]
    fieldData.value.options[index + 1] = temp
  }
}

function getFieldTypeIcon(type: VueformFieldType): string {
  return FIELD_TYPE_ICONS[type] || 'bi-question-circle'
}

function getFieldTypeLabel(type: VueformFieldType): string {
  return FIELD_TYPE_LABELS[type] || type
}

function getFieldTypeDescription(type: VueformFieldType): string {
  return FIELD_TYPE_DESCRIPTIONS[type] || ''
}
</script>

<template>
  <div
    v-if="show"
    class="modal fade show"
    style="display: block; background: rgba(0, 0, 0, 0.5)"
    tabindex="-1"
    @click.self="emit('close')"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">{{ modalTitle }}</h2>
          <button type="button" class="btn-close" @click="emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Tabs -->
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'basic' }"
                @click="activeTab = 'basic'"
              >
                <i class="bi bi-pencil me-1"></i> Basic
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'validation' }"
                @click="activeTab = 'validation'"
              >
                <i class="bi bi-shield-check me-1"></i> Validation
              </button>
            </li>
            <li v-if="requiresOptions" class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'options' }"
                @click="activeTab = 'options'"
              >
                <i class="bi bi-list-ul me-1"></i> Options
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'advanced' }"
                @click="activeTab = 'advanced'"
              >
                <i class="bi bi-gear me-1"></i> Advanced
              </button>
            </li>
          </ul>

          <!-- Basic Tab -->
          <div v-if="activeTab === 'basic'">
            <div class="mb-3">
              <label class="form-label fw-bold">Field Type <span class="text-danger">*</span></label>
              <div class="row g-2">
                <div
                  v-for="(types, category) in fieldTypeCategories"
                  :key="category"
                  class="col-12"
                >
                  <p class="mb-1 text-muted small">{{ category }}</p>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      v-for="type in types"
                      :key="type"
                      type="button"
                      class="btn-sm"
                      :class="
                        fieldData.type === type ? 'bb-btn-primary-cta' : 'bb-btn-secondary-cta'
                      "
                      @click="fieldData.type = type"
                      :title="getFieldTypeDescription(type)"
                    >
                      <i :class="getFieldTypeIcon(type)" class="me-1"></i>
                      {{ getFieldTypeLabel(type) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Field Label <span class="text-danger">*</span></label>
              <input
                v-model="fieldData.label"
                type="text"
                class="form-control"
                placeholder="e.g., Email Address"
              />
            </div>

            <div class="mb-3">
              <label class="form-label">Field Name <span class="text-danger">*</span></label>
              <input
                v-model="fieldData.name"
                type="text"
                class="form-control"
                placeholder="e.g., email_address"
              />
              <small class="text-muted">Unique identifier for this field (snake_case)</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Placeholder</label>
              <input
                v-model="fieldData.placeholder"
                type="text"
                class="form-control"
                placeholder="e.g., you@example.com"
              />
            </div>

            <div class="mb-3">
              <label class="form-label">Help Text</label>
              <textarea
                v-model="fieldData.helpText"
                class="form-control"
                rows="2"
                placeholder="Additional guidance for users"
              ></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Default Value</label>
              <input
                v-model="fieldData.defaultValue"
                type="text"
                class="form-control"
                placeholder="Pre-filled value"
              />
            </div>

            <div class="mb-3">
              <label class="form-label">On Publish to Existing Subscribers</label>
              <select v-model="fieldData.onUpgrade" class="form-select">
                <option :value="undefined">Keep subscriber's value (default)</option>
                <option value="Overwrite">Overwrite with new default</option>
              </select>
              <small class="text-muted">
                Applies when an existing subscriber accepts a new product version.
                <strong>Keep</strong> protects their data;
                <strong>Overwrite</strong> stamps this field's default over
                whatever they had — for forced migrations like a changed
                endpoint URL.
              </small>
            </div>
          </div>

          <!-- Validation Tab -->
          <div v-if="activeTab === 'validation'">
            <div class="mb-3 form-check">
              <input
                v-model="fieldData.validation!.required"
                type="checkbox"
                class="form-check-input"
                id="required"
              />
              <label class="form-check-label" for="required">
                <strong>Required Field</strong>
                <small class="d-block text-muted">User must fill this field</small>
              </label>
            </div>

            <div class="mb-3 form-check">
              <input
                v-model="fieldData.validation!.email"
                type="checkbox"
                class="form-check-input"
                id="email"
              />
              <label class="form-check-label" for="email">
                <strong>Email Format</strong>
                <small class="d-block text-muted">Validate as email address</small>
              </label>
            </div>

            <div class="mb-3 form-check">
              <input
                v-model="fieldData.validation!.url"
                type="checkbox"
                class="form-check-input"
                id="url"
              />
              <label class="form-check-label" for="url">
                <strong>URL Format</strong>
                <small class="d-block text-muted">Validate as web URL</small>
              </label>
            </div>

            <div class="mb-3 form-check">
              <input
                v-model="fieldData.validation!.numeric"
                type="checkbox"
                class="form-check-input"
                id="numeric"
              />
              <label class="form-check-label" for="numeric">
                <strong>Numeric Only</strong>
                <small class="d-block text-muted">Only numbers allowed</small>
              </label>
            </div>

            <div class="row">
              <div class="col-6 mb-3">
                <label class="form-label">Min Length</label>
                <input
                  v-model.number="fieldData.validation!.minLength"
                  type="number"
                  class="form-control"
                  min="0"
                />
              </div>
              <div class="col-6 mb-3">
                <label class="form-label">Max Length</label>
                <input
                  v-model.number="fieldData.validation!.maxLength"
                  type="number"
                  class="form-control"
                  min="0"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-6 mb-3">
                <label class="form-label">Min Value</label>
                <input
                  v-model.number="fieldData.validation!.minValue"
                  type="number"
                  class="form-control"
                />
              </div>
              <div class="col-6 mb-3">
                <label class="form-label">Max Value</label>
                <input
                  v-model.number="fieldData.validation!.maxValue"
                  type="number"
                  class="form-control"
                />
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Custom Regex Pattern</label>
              <input
                v-model="fieldData.validation!.pattern"
                type="text"
                class="form-control"
                placeholder="e.g., ^[A-Z0-9]+$"
              />
              <small class="text-muted">Regular expression for custom validation</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Custom Error Message</label>
              <input
                v-model="fieldData.validation!.customMessage"
                type="text"
                class="form-control"
                placeholder="Custom validation error message"
              />
            </div>
          </div>

          <!-- Options Tab -->
          <div v-if="activeTab === 'options' && requiresOptions">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h3 class="mb-0">Field Options</h3>
              <button type="button" class="bb-btn-secondary-cta btn-sm" @click="addOption">
                <i class="bi bi-plus-circle me-1"></i> Add Option
              </button>
            </div>

            <div v-if="!fieldData.options || fieldData.options.length === 0" class="alert alert-info">
              <i class="bi bi-info-circle me-2"></i>
              No options added. Click "Add Option" to create choices for this field.
            </div>

            <div v-else class="list-group">
              <div
                v-for="(option, index) in fieldData.options"
                :key="index"
                class="list-group-item"
              >
                <div class="row g-2">
                  <div class="col-5">
                    <input
                      v-model="option.label"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Label"
                    />
                  </div>
                  <div class="col-5">
                    <input
                      v-model="option.value"
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Value"
                    />
                  </div>
                  <div class="col-2 d-flex gap-1">
                    <button
                      type="button"
                      class="bb-btn-secondary-cta btn-sm"
                      title="Move up"
                      :disabled="index === 0"
                      @click="moveOptionUp(index)"
                    >
                      <i class="bi bi-arrow-up"></i> Up
                    </button>
                    <button
                      type="button"
                      class="bb-btn-secondary-cta btn-sm"
                      title="Move down"
                      :disabled="index === fieldData.options!.length - 1"
                      @click="moveOptionDown(index)"
                    >
                      <i class="bi bi-arrow-down"></i> Down
                    </button>
                    <button
                      type="button"
                      class="bb-btn-danger btn-sm"
                      @click="removeOption(index)"
                    >
                      <i class="bi bi-trash"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Advanced Tab -->
          <div v-if="activeTab === 'advanced'">
            <div class="mb-3">
              <label class="form-label">Column Width</label>
              <select v-model.number="fieldData.columns" class="form-select">
                <option :value="12">Full Width (12 columns)</option>
                <option :value="6">Half Width (6 columns)</option>
                <option :value="4">Third Width (4 columns)</option>
                <option :value="3">Quarter Width (3 columns)</option>
              </select>
              <small class="text-muted">Bootstrap grid columns (1-12)</small>
            </div>

            <div class="mb-3 form-check">
              <input
                v-model="fieldData.readonly"
                type="checkbox"
                class="form-check-input"
                id="readonly"
              />
              <label class="form-check-label" for="readonly">
                <strong>Read-only</strong>
                <small class="d-block text-muted">User cannot edit this field</small>
              </label>
            </div>

            <div class="mb-3 form-check">
              <input
                v-model="fieldData.hidden"
                type="checkbox"
                class="form-check-input"
                id="hidden"
              />
              <label class="form-check-label" for="hidden">
                <strong>Hidden</strong>
                <small class="d-block text-muted">Field is not visible to users</small>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="emit('close')">
            Cancel
          </button>
          <button type="button" class="bb-btn-primary-cta" @click="handleSave">
            <i class="bi bi-check-circle me-1"></i>
            {{ isEditMode ? 'Update Field' : 'Add Field' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show" @click="emit('close')"></div>
</template>

<style scoped lang="scss">
.modal.show {
  overflow-y: auto;
}

.nav-tabs {
  --bs-nav-tabs-border-width: 0;
  --bs-nav-tabs-link-active-bg: transparent;
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  border-bottom-color: var(--bs-primary);
}
</style>
